import { API_CONFIG } from '../config/api';
import { storage } from '../utils/storage';

interface RequestOptions extends RequestInit {
  skipAuth?: boolean;
}

class ApiService {
  private baseURL: string;

  constructor() {
    this.baseURL = API_CONFIG.baseURL;
  }

  private getHeaders(skipAuth = false): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (!skipAuth) {
      const token = storage.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        error: {
          message: response.statusText || 'An error occurred',
          status: response.status,
        },
      }));

      // Handle Strapi error format
      // Strapi returns errors in format: { error: { message: "...", details: {...} } }
      // or sometimes: { data: null, error: { status: 400, message: "..." } }
      let errorMessage = 'An error occurred';
      
      if (errorData.error) {
        if (errorData.error.message) {
          errorMessage = errorData.error.message;
        } else if (Array.isArray(errorData.error)) {
          // Handle array of errors
          errorMessage = errorData.error.map((err: any) => err.message || err).join(', ');
        }
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }

      const error = new Error(errorMessage);
      console.error('API Error:', errorData, 'Extracted message:', errorMessage);
      throw error;
    }

    return response.json();
  }

  async get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'GET',
      headers: this.getHeaders(options?.skipAuth),
      ...options,
    });

    return this.handleResponse<T>(response);
  }

  async post<T>(
    endpoint: string,
    data?: any,
    options?: RequestOptions
  ): Promise<T> {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'POST',
      headers: this.getHeaders(options?.skipAuth),
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });

    return this.handleResponse<T>(response);
  }

  async put<T>(
    endpoint: string,
    data?: any,
    options?: RequestOptions
  ): Promise<T> {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'PUT',
      headers: this.getHeaders(options?.skipAuth),
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });

    return this.handleResponse<T>(response);
  }

  async delete<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'DELETE',
      headers: this.getHeaders(options?.skipAuth),
      ...options,
    });

    return this.handleResponse<T>(response);
  }
}

export const apiService = new ApiService();
